import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { getDatabase, ref, get, onValue, off } from 'firebase/database';
import { useAuth } from '../context/AuthContext';
import { schoolDetails as defaultSchoolInfo } from '../schoolInfo';
import './MasterReportSheet.css';
import logo from '../pages/logo.png';

const ALL_TERMS = ['FIRST TERM', 'SECOND TERM', 'THIRD TERM'];
const PSYCHOMOTOR_SKILLS = ['Handwriting', 'Verbal Fluency', 'Games and sports', 'Gymnastics', 'Music', 'Construction'];
const AFFECTIVE_AREAS = ['Attentiveness', 'Punctuality', 'Neatness', 'Politeness', 'Relationship with others', 'Honesty'];

const getSubjectGradeAndRemark = (score) => {
    const s = Math.round(score);
    if (s >= 75) return { grade: 'A1', remark: 'Distinction' };
    if (s >= 70) return { grade: 'B2', remark: 'Excellent' };
    if (s >= 65) return { grade: 'B3', remark: 'Very Good' };
    if (s >= 60) return { grade: 'C4', remark: 'Good' };
    if (s >= 55) return { grade: 'C5', remark: 'Credit' };
    if (s >= 50) return { grade: 'C6', remark: 'Credit' };
    if (s >= 45) return { grade: 'D7', remark: 'Pass' };
    if (s >= 40) return { grade: 'E8', remark: 'Pass' };
    return { grade: 'F9', remark: 'Fail' };
};

const getPrincipalRemark = (average) => {
    if (average >= 85) return 'Brilliant performance. Keep it up.';
    if (average >= 75) return 'An impressive performance. Keep it up.';
    if (average >= 65) return "An excellent result. Don't rest on your oar.";
    if (average >= 50) return 'A fair result. You can do better.';
    if (average >= 40) return 'A fair performance. Work harder.';
    return 'More efforts need to be made to improve.';
};

const MasterReportSheet = () => {
    const { currentUser } = useAuth();
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const db = getDatabase();

    const [userData, setUserData] = useState(null);
    const [allClasses, setAllClasses] = useState([]);
    const [processedData, setProcessedData] = useState({ results: [], subjects: [] });
    const [selectedClass, setSelectedClass] = useState(query.get('class') || '');
    const [selectedSession, setSelectedSession] = useState(query.get('session') || '');
    const [selectedTerm, setSelectedTerm] = useState(query.get('term') || '');
    const [schoolInfo, setSchoolInfo] = useState(defaultSchoolInfo);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [gradingStructure, setGradingStructure] = useState({});
    const [subjectDetails, setSubjectDetails] = useState({});
    const [traitsData, setTraitsData] = useState({});
    const [studentTermAverages, setStudentTermAverages] = useState({});
    const [paymentStatus, setPaymentStatus] = useState({ paid: false, checking: true });

    const sessions = useMemo(() => Array.from({ length: 5 }, (_, i) => {
        const year = new Date().getFullYear() - i;
        return `${year}/${year + 1}`;
    }), []);

    useEffect(() => {
        const appSettingsRef = ref(db, 'appSettings/currentSession');
        const unsub = onValue(appSettingsRef, (snapshot) => {
            if (snapshot.exists()) {
                const { session, term } = snapshot.val();
                if (!selectedSession) setSelectedSession(session);
                if (!selectedTerm) setSelectedTerm(term);
            }
        });
        return () => unsub();
    }, [db, selectedSession, selectedTerm]);

    useEffect(() => {
        if (!currentUser) return;
        const userRef = ref(db, `users/${currentUser.uid}`);
        const unsubscribe = onValue(userRef, (snapshot) => {
            const data = snapshot.val();
            if (data) {
                setUserData(data);
                const role = data.role?.toLowerCase();
                if (role === 'student' && !selectedClass) setSelectedClass(data.class || data.studentClass || '');
                else if (role === 'form-master' && !selectedClass) setSelectedClass(data.assignedClass || '');
            }
        });
        return () => unsubscribe();
    }, [currentUser, db, selectedClass]);
    
    useEffect(() => {
        if (!currentUser || userData?.role?.toLowerCase() !== 'student') {
            setPaymentStatus({ paid: true, checking: false });
            return;
        }
        const invoicesRef = ref(db, `invoices/${currentUser.uid}`);
        const callback = onValue(invoicesRef, (snapshot) => {
            const hasPending = snapshot.exists() && Object.values(snapshot.val()).some(inv => inv.status.toLowerCase() !== 'paid');
            setPaymentStatus({ paid: !hasPending, checking: false });
        });
        return () => off(invoicesRef, 'value', callback);
    }, [currentUser, db, userData]);

    useEffect(() => {
        const role = userData?.role?.toLowerCase();
        if (['admin', 'principal'].includes(role)) {
            const classesRef = ref(db, 'classes');
            const unsubscribe = onValue(classesRef, (snapshot) => {
                if (snapshot.exists()) setAllClasses(Object.keys(snapshot.val()).sort());
            });
            return () => unsubscribe();
        }
    }, [userData, db]);

    useEffect(() => {
        if (!selectedClass) return;
        const name = selectedClass.toLowerCase();
        let structure = {};
        if (name.includes('playgroup') || name.includes('kg') || name.includes('nursery')) {
            structure = { Assignment: 20, Test: 20, Exam: 60 };
        } else if (name.includes('primary')) {
          structure = { '1st Assign': 10, 'Test 1': 15, 'Test 2': 15, 'Exam': 60 };
        } else if (name.includes('jss')) {
          structure = { '1st Assign': 10, '2nd Assign': 10, '1st Test': 10, '2nd Test': 10, 'Exam': 60 };
        } else if (name.includes('sss')) {
          structure = { '1st Assign': 5, '2nd Assign': 5, '1st Test': 10, '2nd Test': 10, 'Exam': 70 };
        }
        setGradingStructure(structure);
    }, [selectedClass]);

    const fetchReportData = useCallback(async () => {
        if (!selectedClass || !selectedSession || !selectedTerm) {
            setError("Please select a class, session, and term.");
            return;
        }
        setLoading(true);
        setError(null);
        
        if (!paymentStatus.paid) {
            setProcessedData({ results: [], subjects: [] });
            setLoading(false);
            return;
        }

        try {
            const sessionPath = selectedSession.replace('/', '-');
            const termPath = selectedTerm.toUpperCase();
            const [schoolSnap, usersSnap, traitsSnap, allScoresSnap, attendanceSnap] = await Promise.all([
                get(ref(db, 'school')),
                get(ref(db, 'users')),
                get(ref(db, `traits/${sessionPath}/${termPath}/${selectedClass}`)),
                get(ref(db, `ca_scores/${sessionPath}`)),
                get(ref(db, `attendance/${sessionPath}/${termPath}/${selectedClass}`))
            ]);

            setSchoolInfo(schoolSnap.exists() ? schoolSnap.val() : defaultSchoolInfo);
            const allUsers = usersSnap.val() || {};
            setTraitsData(traitsSnap.val() || {});
            const allScores = allScoresSnap.val() || {};
            const scoresData = allScores[termPath]?.[selectedClass] || {};
            const classSubjects = Object.keys(scoresData);
            const attendanceData = attendanceSnap.val() || {};

            let filteredStudents = Object.entries(allUsers)
                .filter(([_, user]) => user.role?.toLowerCase() === 'student' && (user.class?.replace(/\s+/g, '').toLowerCase() === selectedClass?.replace(/\s+/g, '').toLowerCase() || user.studentClass?.replace(/\s+/g, '').toLowerCase() === selectedClass?.replace(/\s+/g, '').toLowerCase()))
                .map(([id, student]) => ({ ...student, id, regNo: student.regNo || student.registrationNumber || 'N/A', fullName: `${student.firstName || ''} ${student.lastName || ''}`.trim() }));
            
            if (userData?.role?.toLowerCase() === 'student') {
                filteredStudents = filteredStudents.filter(s => s.id === currentUser.uid);
            }

            if (filteredStudents.length === 0) {
                setLoading(false);
                setError("No students found for the selected class.");
                return;
            }

            const termAverages = {};
            filteredStudents.forEach(student => {
                termAverages[student.id] = {};
                ALL_TERMS.forEach(term => {
                    const termScoresData = allScores[term]?.[selectedClass] || {};
                    const subjectsInTerm = Object.keys(termScoresData);
                    let totalScore = 0;
                    subjectsInTerm.forEach(subject => {
                        const studentSubjectScores = termScoresData[subject]?.[student.id] || {};
                        totalScore += Object.values(studentSubjectScores).reduce((acc, s) => acc + (Number(s) || 0), 0);
                    });
                    termAverages[student.id][term] = subjectsInTerm.length > 0 ? totalScore / subjectsInTerm.length : 0;
                });
            });
            setStudentTermAverages(termAverages);
            
            const subjectDetailsData = {};
            classSubjects.forEach(subject => {
                const scoresArray = filteredStudents.map(student => {
                    const total = Object.values(scoresData[subject]?.[student.id] || {}).reduce((a,b)=>a+(Number(b)||0),0);
                    return { id: student.id, total };
                });
                scoresArray.sort((a,b)=>b.total - a.total);
                
                const positions = {};
                scoresArray.forEach((score, index) => {
                    const rank = (index > 0 && score.total === scoresArray[index-1].total) ? positions[scoresArray[index-1].id] : index + 1;
                    positions[score.id] = rank;
                });

                const totals = scoresArray.map(s=>s.total);
                subjectDetailsData[subject] = {
                    classAverage: totals.length > 0 ? totals.reduce((a, b) => a + b, 0) / totals.length : 0,
                    classHighest: totals.length > 0 ? Math.max(...totals) : 0,
                    classLowest: totals.length > 0 ? Math.min(...totals) : 0,
                    positions
                };
            });
            setSubjectDetails(subjectDetailsData);

            const studentResults = filteredStudents.map(student => {
                let totalScore = 0;
                const scoresBySubject = {};
                classSubjects.forEach(subject => {
                    const scores = scoresData[subject]?.[student.id] || {};
                    const subjectTotal = Object.values(scores).reduce((a,b)=>a+(Number(b)||0),0);
                    scoresBySubject[subject] = { ...scores, total: subjectTotal };
                    totalScore += subjectTotal;
                });
                const average = classSubjects.length > 0 ? totalScore / classSubjects.length : 0;
                return { ...student, scores: scoresBySubject, totalScore, average, subjects: classSubjects, daysPresent: attendanceData[student.id]?.presentCount || 'N/A' };
            });

            studentResults.sort((a,b)=>b.average - a.average);
            studentResults.forEach((s,i)=>s.position = i+1);

            setProcessedData({ results: studentResults, subjects: classSubjects });

        } catch(err) {
            console.error(err);
            setError(`Error fetching report: ${err.message}`);
        } finally {
            setLoading(false);
        }
    }, [db, selectedClass, selectedSession, selectedTerm, currentUser, userData, paymentStatus.paid]);

    const role = userData?.role?.toLowerCase();
    const canSelectFilters = ['admin','principal','form-master'].includes(role);
    return (
        <div className="master-report-sheet">
            <div className="no-print">
                <h3>Master Report Sheet</h3>
                {error && <div className="alert alert-danger">{error}</div>}
                <div className="row">
                    {canSelectFilters && (
                        <>
                            <div className="col-md-3">
                                <label>Session</label>
                                <select className="form-select" value={selectedSession} onChange={e => setSelectedSession(e.target.value)}>
                                    {sessions.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                            <div className="col-md-3">
                                <label>Term</label>
                                <select className="form-select" value={selectedTerm} onChange={e => setSelectedTerm(e.target.value)}>
                                    {ALL_TERMS.map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                            </div>
                            <div className="col-md-3">
                                <label>Class</label>
                                <select className="form-select" value={selectedClass} onChange={e => setSelectedClass(e.target.value)}>
                                    <option value="">-- Select Class --</option>
                                    {role === 'form-master' ? 
                                        <option value={userData.assignedClass}>{userData.assignedClass}</option> : 
                                        allClasses.map(c => <option key={c} value={c}>{c}</option>)
                                    }
                                </select>
                            </div>
                        </>
                    )}
                    <div className="col-md-3 d-flex align-items-end">
                        <button className="btn btn-primary me-2" onClick={fetchReportData} disabled={loading || !paymentStatus.paid || !selectedSession || !selectedTerm}>
                            {loading ? 'Loading...' : 'Generate Report'}
                        </button>
                        <button className="btn btn-secondary" onClick={() => window.print()} disabled={processedData.results.length === 0}>Print</button>
                    </div>
                </div>
                {!paymentStatus.paid && !paymentStatus.checking && <div className="alert alert-warning mt-3">Please settle any outstanding fees to view the report card.</div>}
            </div>

            {loading && <div className="text-center"><div className="spinner-border"></div></div>}

            {processedData.results.map((student, index) => (
                <div key={student.id} className="report-card-page">
                    <table style={{ borderCollapse: 'collapse', width: '100%' }} border="0">
                        <tbody>
                            <tr>
                                <td style={{ width: '20%' }} rowSpan="4"><img src={schoolInfo.logoUrl || logo} width="100" height="100" alt="logo" /></td>
                                <td style={{ textAlign: 'center', width: '80%' }} colSpan="2">
                                    <span style={{ fontFamily: `'arial black', sans-serif`, color: '#236fa1', fontSize: '24pt' }}><strong>{schoolInfo.schoolName}</strong></span>
                                </td>
                            </tr>
                            <tr>
                                <td style={{ width: '80%', textAlign: 'center' }} colSpan="2">
                                    <span style={{ color: '#e03e2d' }}><strong><span style={{ fontFamily: `'times new roman', times, serif` }}><em>({schoolInfo.motto})</em></span></strong></span>
                                </td>
                            </tr>
                            <tr>
                                <td style={{ width: '80%', textAlign: 'center' }} colSpan="2">
                                    <strong><span style={{ color: '#e03e2d' }}>{schoolInfo.address}</span></strong>
                                </td>
                            </tr>
                            <tr>
                                <td style={{ width: '40%', textAlign: 'center' }}><span style={{ color: '#3598db' }}><strong><span style={{ fontFamily: `'book antiqua', palatino, serif` }}>{schoolInfo.phone}</span></strong></span></td>
                                <td style={{ width: '40%', textAlign: 'right' }}><span style={{ color: '#3598db' }}><strong><span style={{ fontFamily: `'book antiqua', palatino, serif` }}>{schoolInfo.email}</span></strong></span></td>
                            </tr>
                        </tbody>
                    </table>
                    <hr />
                    <table style={{ borderCollapse: 'collapse', width: '100%' }} border="0">
                        <tbody>
                            <tr>
                                <td style={{ width: '100%', textAlign: 'center' }}><span style={{ textDecoration: 'underline', color: '#236fa1' }}><strong>Terminal Progress Report for {selectedTerm.toUpperCase()}, {selectedSession} session</strong></span></td>
                            </tr>
                        </tbody>
                    </table>

                    <table style={{ borderCollapse: 'collapse', width: '100%' }} border="0">
                        <tbody>
                            <tr>
                                <td colSpan="2"><span style={{ fontSize: '10pt' }}><strong>Name of Student</strong>: <span style={{ fontFamily: `'arial black', sans-serif` }}><strong>{student.fullName}</strong></span></span></td>
                                <td><span style={{ fontSize: '10pt' }}><strong>Days school opened</strong>: {schoolInfo.daysOpened || 'N/A'}</span></td>
                                <td rowSpan="3" style={{verticalAlign:'top'}}><img width="100" height="100" style={{ borderRadius: '50%' }} src={student.photoUrl || 'https://via.placeholder.com/100'} alt="avatar" /></td>
                            </tr>
                            <tr>
                                <td><span style={{ fontSize: '10pt' }}><strong>Admission No</strong>: {student.regNo}</span></td>
                                <td><span style={{ fontSize: '10pt' }}><strong>Sex</strong>: {student.gender}</span></td>
                                <td><span style={{ fontSize: '10pt' }}><strong>Days present</strong>: {student.daysPresent}</span></td>
                            </tr>
                            <tr>
                                <td><span style={{ fontSize: '10pt' }}><strong>Class</strong>: {student.class || student.studentClass}</span></td>
                                <td><span style={{ fontSize: '10pt' }}><strong>No. in Class</strong>: {processedData.results.length}</span></td>
                                <td><span style={{ fontSize: '10pt' }}><strong>Position</strong>: {student.position}</span></td>
                            </tr>
                        </tbody>
                    </table>

                    <div style={{ fontFamily: `'comic sans ms', sans-serif`, color: '#3598db' }}><strong>COGNITIVE DOMAIN</strong></div>
                    <table className="table-tiny table-striped small">
                        <thead>
                            <tr>
                                <th>Subject</th>
                                {Object.keys(gradingStructure).map(field => <th key={field}>{`${field} (${gradingStructure[field]?.toFixed(2) || '0'}%)`}</th>)}
                                <th>Total</th><th>Grade</th><th>Class Average</th><th>Class Lowest</th><th>Class Highest</th><th>Position</th><th>Remark</th>
                            </tr>
                        </thead>
                        <tbody>
                            {student.subjects.map(subject => {
                                const subjectScores = student.scores[subject] || {};
                                const { grade, remark } = getSubjectGradeAndRemark(subjectScores.total);
                                const details = subjectDetails[subject];
                                return (
                                    <tr key={subject}>
                                        <td>{subject}</td>
                                        {Object.keys(gradingStructure).map(field => <td key={field}>{subjectScores[field] || ''}</td>)}
                                        <td>{subjectScores.total?.toFixed(2)}</td>
                                        <td>{grade}</td>
                                        <td>{details?.classAverage?.toFixed(2)}</td>
                                        <td>{details?.classLowest}</td>
                                        <td>{details?.classHighest}</td>
                                        <td>{details?.positions[student.id]}</td>
                                        <td>{remark}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>

                    <table style={{width: '100%', borderCollapse: 'collapse'}} border="0">
                        <tbody>
                            <tr>
                                <td style={{width:'33.33%'}}>Total: <span style={{color:'#e03e2d'}}><strong>{student.totalScore.toFixed(2)}</strong></span></td>
                                <td style={{width:'33.33%'}}>Subjects: <span style={{color:'#e03e2d'}}><strong>{student.subjects.length}</strong></span></td>
                                <td style={{width:'33.33%'}}>Average: <span style={{color:'#e03e2d'}}><strong>{student.average.toFixed(2)}</strong></span></td>
                            </tr>
                        </tbody>
                    </table>
                    <hr />
                    <table style={{width: '100%', borderCollapse:'collapse'}} border="0">
                        <thead>
                            <tr>
                                {ALL_TERMS.map(term => <td key={term}><span style={{color: '#3598db'}}><strong>{term} Average</strong></span></td>)}
                                <td><span style={{color: '#3598db'}}><strong>Cumulative Average</strong></span></td>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                {ALL_TERMS.map(term => {
                                    const avg = studentTermAverages[student.id]?.[term];
                                    return <td key={term}><span style={{color: '#d81414'}}><strong><span style={{fontSize: '10pt'}}>{avg?.toFixed(2) || '0.00'}</span></strong></span></td>;
                                })}
                                <td><span style={{color: '#d81414'}}><strong><span style={{fontSize: '10pt'}}>{student.average.toFixed(2)}</span></strong></span></td>
                            </tr>
                        </tbody>
                    </table>
                    <hr/>
                    <table style={{width:'100%'}} border="0">
                        <tbody>
                            <tr>
                                <td style={{width:'33.33%', verticalAlign:'top'}}>
                                    <div style={{color:'#e03e2d', textAlign:'center'}}><strong>RATING</strong></div>
                                    <table className='table-tiny table-striped small'>
                                        <thead><tr><th colSpan="2"><strong>Psychomotor Skills</strong></th></tr></thead>
                                        <tbody>
                                            {PSYCHOMOTOR_SKILLS.map(skill => <tr key={skill}><td>{skill}</td><td>{traitsData[student.id]?.psychomotor?.[skill] || 'N/A'}</td></tr>)}
                                        </tbody>
                                    </table>
                                </td>
                                <td style={{width:'33.33%', verticalAlign:'top'}}>
                                <div style={{color:'#e03e2d', textAlign:'center'}}><strong>&nbsp;</strong></div>
                                    <table className='table-tiny table-striped small'>
                                        <thead><tr><th colSpan="2"><strong>Affective Areas</strong></th></tr></thead>
                                        <tbody>
                                            {AFFECTIVE_AREAS.map(area => <tr key={area}><td>{area}</td><td>{traitsData[student.id]?.affective?.[area] || 'N/A'}</td></tr>)}
                                        </tbody>
                                    </table>
                                </td>
                                <td style={{width:'33.33%', verticalAlign:'top'}}>
                                <div style={{color:'#e03e2d', textAlign:'center'}}><strong>GRADING SCALE</strong></div>
                                    <table className='table-tiny table-striped small'>
                                    <tbody>
                                        <tr><td>A1</td><td>75.00 - 100.00</td></tr>
                                        <tr><td>B2</td><td>70.00 - 74.99</td></tr>
                                        <tr><td>B3</td><td>65.00 - 69.99</td></tr>
                                        <tr><td>C4</td><td>60.00 - 64.99</td></tr>
                                        <tr><td>C5</td><td>55.00 - 59.99</td></tr>
                                        <tr><td>C6</td><td>50.00 - 54.99</td></tr>
                                        <tr><td>D7</td><td>45.00 - 49.99</td></tr>
                                        <tr><td>E8</td><td>40.00 - 44.99</td></tr>
                                        <tr><td>F9</td><td>0.00 - 39.99</td></tr>
                                    </tbody>
                                    </table>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <hr />
                    <table style={{width:'100%', fontSize:'10pt'}}>
                        <tbody>
                            <tr>
                                <td><strong>Class master's name:</strong></td>
                                <td>{schoolInfo.formMaster?.[selectedClass] || 'N/A'}</td>
                                <td></td>
                            </tr>
                            <tr>
                                <td><strong>Class master's remark:</strong></td>
                                <td>{getPrincipalRemark(student.average)}</td>
                                <td></td>
                            </tr>
                            <tr>
                                <td><strong>Principal's remark:</strong></td>
                                <td>{getPrincipalRemark(student.average)}</td>
                                <td style={{textAlign:'center'}}>
                                    <div style={{color:'#169179'}}>________________</div>
                                    <div style={{color:'#169179'}}><strong>Signature</strong></div>
                                </td>
                            </tr>
                             <tr>
                                <td><strong>Term ends:</strong> {schoolInfo.termEnds}</td>
                                <td><strong>Next term begins:</strong> {schoolInfo.nextTermBegins}</td>
                                <td></td>
                            </tr>
                        </tbody>
                    </table>
                    {index < processedData.results.length - 1 && <div className="pagebreak">&nbsp;</div>}
                </div>
            ))}
        </div>
    );
}

export default MasterReportSheet;
